import { Icon, Skeleton } from '@scale/scaleui';
import { ReviewLevel } from '@scale/shared-types';
import { OutlinePanel } from 'react-scale/remotasks.com/components/Expert/OutlinePanel';
import { CourseOutlineSection } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/components/CourseOutlineSection';
import { TutorialBenchmarkSection } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/components/TutorialBenchmarkSection';
import { TaskTrainingSection } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/components/TaskTrainingSection';
import { TaskWallSection } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/components/TaskWallSection';
import { BenchmarkWallSection } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/components/BenchmarkWallSection';
import { useProjectOnboardingFlow } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/hooks/useProjectOnboardingFlow';
import {
  useProjectOutlineStore,
  useIsInTraining,
  useGetOnboardingState,
} from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/stores/projectOutlineStore';
import { CurrentTaskSection } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/components/CurrentTaskSection';
import { OnboardingStepType } from '@scale/shared/onboardingControlPanel/index';
import { useChatThemeStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';
import { analytics } from 'react-scale/lib/analytics/segment-source-scalers';
import { getPageLoadSingleton } from '@scale/frontend-lib/globalSingletons';
import { TaskOutline } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/components/TaskOutline';
import { Claim } from '@scale/shared/types/queue';
import { useState } from 'react';
import { ProjectOutlineOnboardingStates } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/types';

type ProjectOnboardingOutlineProps = {
  onboardingFlowId?: string;
  projectId?: string;
  reviewLevel?: ReviewLevel;
  displayHovered?: boolean;
  fromCourse?: boolean;
  claim?: Claim;
  setCurrentSectionIndex?: (index: number) => void;
};

export const ProjectOnboardingOutline = ({
  onboardingFlowId,
  reviewLevel,
  projectId,
  displayHovered,
  fromCourse,
  claim,
  setCurrentSectionIndex,
}: ProjectOnboardingOutlineProps) => {
  const { isLoadingProjectOnboardingFlow, isLoadingCourses } = useProjectOnboardingFlow({
    onboardingFlowId,
    reviewLevel,
    projectId,
  });

  const isVisible = useProjectOutlineStore(state => state.isVisible);
  const hideProjectOutline = useProjectOutlineStore(state => state.hideProjectOutline);
  const trainingNodes = useProjectOutlineStore(state => state.trainingNodes);
  const assessmentNodes = useProjectOutlineStore(state => state.assessmentNodes);
  const taskingNodes = useProjectOutlineStore(state => state.taskingNodes);
  const isInTraining = useIsInTraining();
  const { theme: chatTheme } = useChatThemeStore();
  const onboardingState = useGetOnboardingState();

  const handleClose = () => {
    analytics.track('Project onboarding', {
      onboardingFlowId,
      pageLoadId: getPageLoadSingleton(),
      event: 'outline_closed',
      onboardingState,
    });
    hideProjectOutline();
  };

  const assessmentSectionTitle = `Part ${trainingNodes.length > 0 ? 'II' : 'I'}: Assessment`;
  const taskingSectionTitle = `Part ${
    trainingNodes.length > 0 && assessmentNodes.length > 0
      ? 'III'
      : trainingNodes.length > 0 || assessmentNodes.length > 0
      ? 'II'
      : 'I'
  }: Tasking`;

  type AccordionProps = {
    title: React.ReactNode;
    content: React.ReactNode;
    startsOpened?: boolean;
  };
  const Accordion = ({ title, content, startsOpened = false }: AccordionProps) => {
    const [isOpen, setIsOpen] = useState(startsOpened);

    return (
      <>
        <button
          onClick={() => setIsOpen(prev => !prev)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center space-x-2">
            {title}
            <span>{isOpen ? <Icon icon="chevron-up" /> : <Icon icon="chevron-down" />}</span>
          </div>
        </button>
        {isOpen && content}
      </>
    );
  };
  return (
    <OutlinePanel
      title={taskingNodes.length > 0 ? 'Project Onboarding Outline' : 'Project Outline'}
      showOutline={isVisible}
      onClose={handleClose}
      displayHovered={displayHovered}
      className={fromCourse ? 'fixed z-40' : ''}
      theme={isInTraining ? undefined : (chatTheme as string)}
    >
      {isLoadingProjectOnboardingFlow ? (
        <>
          <Skeleton className="w-full h-24" />
          <Skeleton className="w-full h-24" />
          <Skeleton className="w-full h-24" />
          <Skeleton className="w-full h-24" />
        </>
      ) : (
        <>
          {trainingNodes.length > 0 && (
            <Accordion
              title={<div className="text-sm text-neutral-700">Part I: Onboarding</div>}
              content={
                <>
                  {!isLoadingCourses &&
                    trainingNodes.map((node, index) => {
                      if ('courseTitle' in node) {
                        return (
                          <CourseOutlineSection
                            key={index}
                            node={node}
                            loading={isLoadingCourses}
                            setCurrentSectionIndex={setCurrentSectionIndex}
                          />
                        );
                      } else if ('reviewLevel' in node) {
                        return <TutorialBenchmarkSection key={index} node={node} />;
                      } else if ('practiceTaskTitle' in node) {
                        return <TaskTrainingSection key={index} node={node} />;
                      }
                      return null;
                    })}
                </>
              }
              startsOpened={onboardingState === ProjectOutlineOnboardingStates.Training}
            />
          )}
          {assessmentNodes.length > 0 && (
            <Accordion
              title={<div className="text-sm text-neutral-700">{assessmentSectionTitle}</div>}
              content={
                <>
                  <div className="text-xs text-neutral-500">
                    The assessment is graded to ensure you meet the quality standards for this
                    project. A passing grade is required to continue working and start tasking on
                    the project.
                  </div>
                  {assessmentNodes.map((node, index) => {
                    if (node.type === OnboardingStepType.BenchmarkWall) {
                      return <BenchmarkWallSection key={index} node={node} />;
                    } else {
                      return <TaskWallSection key={index} node={node} />;
                    }
                  })}
                  {!fromCourse && onboardingState === ProjectOutlineOnboardingStates.Assessment && (
                    <TaskOutline reviewLevel={reviewLevel} claim={claim} inAssessment={true} />
                  )}
                </>
              }
              startsOpened={onboardingState === ProjectOutlineOnboardingStates.Assessment}
            />
          )}
          {taskingNodes.length > 0 && (
            <Accordion
              title={<div className="text-sm text-neutral-700">{taskingSectionTitle}</div>}
              content={
                <>
                  {taskingNodes.map((node, index) => (
                    <CurrentTaskSection key={index} node={node} />
                  ))}{' '}
                </>
              }
              startsOpened={onboardingState === ProjectOutlineOnboardingStates.Tasking}
            />
          )}
          {!fromCourse && onboardingState !== ProjectOutlineOnboardingStates.Assessment && (
            <TaskOutline reviewLevel={reviewLevel} claim={claim} />
          )}
        </>
      )}
    </OutlinePanel>
  );
};
